/** Resolve the native date/time input from a click on a Quasar/Vuetify field. */
export function resolveNativeDateInput(e) {
    let input = e?.target;
    if (!input || input.tagName !== 'INPUT')
        input = e?.currentTarget?.querySelector?.('input');
    return input && input.tagName === 'INPUT' ? input : null;
}

/**
 * Quasar/Vuetify often do not forward the native `cancel` event from date inputs.
 * Bind directly so dismiss (Escape / outside click) clears open state.
 */
export function bindNativePickerDismiss(input, onDismiss) {
    if (!input || typeof onDismiss !== 'function')
        return;
    if (input._ccNativePickerDismiss === onDismiss)
        return;
    if (input._ccNativePickerDismiss)
        input.removeEventListener('cancel', input._ccNativePickerDismiss);
    input._ccNativePickerDismiss = onDismiss;
    input.addEventListener('cancel', onDismiss);
}

/**
 * Toggle a native date / time / datetime-local picker.
 * Browsers only expose showPicker(); close via blur. When Chromium dismisses the
 * picker on the same click that hits the input, cancel clears open state and a
 * short suppress window prevents an immediate reopen.
 *
 * @param {Event} e
 * @param {{ isOpen: boolean, suppressUntil?: number }} state
 * @param {(ev?: Event) => void} [onDismiss]
 * @returns {{ isOpen: boolean, suppressUntil: number }}
 */
export function toggleNativeDatePicker(e, state, onDismiss) {
    const now = Date.now();
    const suppressUntil = state.suppressUntil || 0;
    const input = resolveNativeDateInput(e);

    if (!input)
        return { isOpen: false, suppressUntil };

    if (onDismiss)
        bindNativePickerDismiss(input, onDismiss);

    if (state.isOpen) {
        e?.preventDefault?.();
        try {
            input.blur();
        } catch (_) { /* ignore */ }
        return { isOpen: false, suppressUntil: now + 300 };
    }

    if (suppressUntil && now < suppressUntil)
        return { isOpen: false, suppressUntil };

    if (typeof input.showPicker === 'function') {
        try {
            input.showPicker();
            return { isOpen: true, suppressUntil: 0 };
        } catch (_) { /* not supported / not a user gesture */ }
    }

    return { isOpen: false, suppressUntil };
}

/** Call when the browser dismisses the picker (cancel / change / blur). */
export function dismissNativeDatePicker(state) {
    if (!state.isOpen)
        return { isOpen: false, suppressUntil: state.suppressUntil || 0 };
    return { isOpen: false, suppressUntil: Date.now() + 300 };
}
